import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/data.css';
import GroupList from './scheduleBuilder/GroupList';
import ContentRangePicker from './scheduleBuilder/ContentRangePicker';
import ChapterTreePicker from './scheduleBuilder/ChapterTreePicker';
import CoursePicker from './scheduleBuilder/CoursePicker';
import MonthPlanner from './scheduleBuilder/MonthPlanner';
import { fetchFaculty } from '../../api/adminApi';

/**
 * Plans a month of classes for a group of batches, across two screens.
 *
 * Screen one is the group list and nothing else. Screen two plans the month for
 * the chosen group, with no group controls competing for attention: the group
 * and its live group were already decided by the click that got here, and the
 * header names them.
 *
 * Rows arrive from the server already split by live group — `Batch.liveGroup`
 * cuts across a batch group (`10C1`-`10C5` are live group 1 and `10C10`-`10C12`
 * are live group 2, all "Class 10 · CBSE · Stream 1") and a live class runs for
 * one live group at a time.
 */
const MonthPlannerSection = () => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [excludedBatchIds, setExcludedBatchIds] = useState([]);
  const [batchesExpanded, setBatchesExpanded] = useState(false);
  // The group's syllabus course is preselected; `groupCourses` sorts it first,
  // ahead of add-ons like `AI 4 Kerala` that sit on every batch too.
  const [courseId, setCourseId] = useState('');
  const [chapter, setChapter] = useState(null);
  const [contentIds, setContentIds] = useState([]);
  // Mirrors the planner's own Mode control, which lives below the chapter picker
  // but decides which material bucket a class is scheduled from.
  const [mode, setMode] = useState('Recorded');
  const [faculty, setFaculty] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchFaculty();
        if (active) setFaculty(data.faculty || data || []);
      } catch (error) {
        // The faculty picker degrades to "unassigned" rather than blocking the plan.
        if (active) setFaculty([]);
      }
    })();
    return () => { active = false; };
  }, []);

  const clearPlan = () => {
    setExcludedBatchIds([]);
    setBatchesExpanded(false);
    setCourseId('');
    setChapter(null);
    setContentIds([]);
  };

  const openGroup = (group) => {
    clearPlan();
    setSelectedGroup(group);
    const courses = group.courses || [];
    const primary = courses.find((course) => course.isPrimary) || courses[0];
    if (primary) setCourseId(String(primary.id));
  };

  // Leaving the planning screen drops the in-progress plan rather than carrying
  // a half-built one onto the next group, where it would silently apply to
  // batches it was never authored for.
  const backToGroups = () => {
    clearPlan();
    setSelectedGroup(null);
  };

  const toggleBatch = (batchId) => {
    setExcludedBatchIds((current) => (
      current.includes(batchId)
        ? current.filter((id) => id !== batchId)
        : [...current, batchId]
    ));
    // The contents picker marks what is already scheduled for these batches, so
    // changing the batch set invalidates the current selection.
    setContentIds([]);
  };

  const activeBatchIds = useMemo(() => {
    if (!selectedGroup) return [];
    return selectedGroup.batches
      .filter((batch) => !excludedBatchIds.includes(batch.id))
      .map((batch) => batch.id);
  }, [selectedGroup, excludedBatchIds]);

  if (!selectedGroup) {
    return (
      <div className="sb-section">
        <GroupList onSelect={openGroup} />
      </div>
    );
  }

  const groupCourses = selectedGroup.courses || [];

  return (
    <div className="sb-section">
      <div className="sb-planhead">
        <button type="button" className="sb-backlink" onClick={backToGroups}>
          ‹ All groups
        </button>
        <div className="sb-planhead-row">
          <h3 className="sb-planhead-title">{selectedGroup.label}</h3>
          <span className="sb-selcount">
            {activeBatchIds.length === selectedGroup.batchCount
              ? `${selectedGroup.batchCount} batches`
              : `${activeBatchIds.length} of ${selectedGroup.batchCount} batches`}
            <button
              type="button"
              className="sb-linkbutton"
              aria-expanded={batchesExpanded}
              onClick={() => setBatchesExpanded((open) => !open)}
            >
              {batchesExpanded ? 'Done' : 'Edit'}
            </button>
          </span>
        </div>

        {batchesExpanded && (
          <div className="sb-batchdrawer">
            {selectedGroup.batches.map((batch) => {
              const isOff = excludedBatchIds.includes(batch.id);
              return (
                <label key={batch.id} className={`sb-batchchip${isOff ? ' off' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!isOff}
                    onChange={() => toggleBatch(batch.id)}
                  />
                  {batch.name || `Batch ${batch.id}`}
                </label>
              );
            })}
          </div>
        )}

        {(selectedGroup.warnings || []).length > 0 && (
          <div className="sb-group-warn">
            {selectedGroup.warnings.map((warning) => <div key={warning}>{warning}</div>)}
          </div>
        )}
      </div>

      <div className="sb-panel">
        <div className="sb-panel-head">
          <h4>1. Chapter &amp; contents</h4>
          <span className="sb-selcount">
            {contentIds.length > 0 ? `${contentIds.length} contents selected` : 'Nothing selected yet'}
          </span>
        </div>

        <div className="sb-pickrow">
          <CoursePicker
            courses={groupCourses}
            selectedCourseId={courseId}
            onSelect={(course) => {
              setCourseId(String(course.id));
              setChapter(null);
              setContentIds([]);
            }}
          />

          <ChapterTreePicker
            courseId={courseId ? Number(courseId) : null}
            selectedChapterId={chapter ? chapter.id : null}
            mode={mode}
            onSelect={(node) => { setChapter(node); setContentIds([]); }}
          />
        </div>

        {groupCourses.length === 0 && (
          <span className="cal-empty">
            No courses are linked to this group&rsquo;s batches, so there is nothing to plan from.
          </span>
        )}

        <ContentRangePicker
          chapterId={chapter ? chapter.id : null}
          chapterPath={chapter ? [...(chapter.path || []), chapter.name] : []}
          batchIds={activeBatchIds}
          mode={mode}
          selectedIds={contentIds}
          onChange={setContentIds}
        />
      </div>

      <MonthPlanner
        group={selectedGroup}
        batchIds={activeBatchIds}
        chapterId={chapter ? chapter.id : null}
        contentIds={contentIds}
        faculty={faculty}
        onModeChange={(nextMode) => { setMode(nextMode); setContentIds([]); }}
      />
    </div>
  );
};

export default MonthPlannerSection;
